import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceLine,
} from 'recharts';

// StackedAreaTile — 100% stacked area for mix charts (product mix, sales
// sources, viability mix). Includes a Volume/Share toggle and optional
// methodology-break marker. Consistent bucket colouring where recognized.

const KNOWN_COLORS = {
  // Deal-type — no yellow on white; BOGO Max moved to terracotta
  'TM Max': '#1D57A5', 'TM Pro': '#5A8C3B', 'BOGO Max': '#C25B1E', 'BOGO Pro': '#F26522',
  'Office Action Response': '#8A2BE2', 'Brand Protection Consult': '#00A5CF', 'Cease & Desist': '#C4162A',
  'Unclassified': '#8A8A8A',
  // Sources
  'Google Ads': '#1D57A5', 'Organic': '#5A8C3B', 'FB Ads - Webinar': '#F26522',
  'Other/Referral/Alumni': '#8A2BE2', 'Alumni': '#8A2BE2',
};

const colorFor = (name, idx) => KNOWN_COLORS[name] || ['#141B4D', '#1D57A5', '#5A8C3B', '#B4530C', '#F26522', '#8A2BE2', '#00A5CF', '#C4162A'][idx % 8];

const GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };
const TOOLTIP = {
  contentStyle: { background: '#FFFFFF', border: '1px solid #141B4D', borderRadius: 0, color: '#141B4D', fontSize: 12 },
};

export function StackedAreaTile({
  label,
  series,          // [{month, label, buckets:{name:count}, total}]
  formula,
  source,
  clock = 'cash',
  height = 220,
  methodologyBreaks = [],
  chartId,         // used to filter methodology breaks
  testId,
  defaultView = 'share',   // 'share' | 'volume'
}) {
  const [view, setView] = useState(defaultView);

  const bucketNames = useMemo(() => {
    const names = new Set();
    for (const p of series || []) Object.keys(p.buckets || {}).forEach((n) => names.add(n));
    return [...names];
  }, [series]);

  const data = useMemo(() => {
    return (series || []).map((p) => {
      const row = { month: p.month, short: p.label ? p.label.slice(0, 3) : '' };
      for (const n of bucketNames) {
        const v = p.buckets?.[n] || 0;
        row[n] = view === 'share' ? (p.total ? v / p.total : 0) : v;
      }
      return row;
    });
  }, [series, bucketNames, view]);

  const rateAxis = view === 'share'
    ? { domain: [0, 1], tickFormatter: (v) => `${Math.round(v * 100)}%` }
    : {};

  const relevantBreaks = methodologyBreaks.filter((b) => (b.applies_to || []).includes(chartId));

  if (!series || series.length === 0) {
    return (
      <div className="border border-dashed border-indie-ink/20 bg-white p-4 text-xs text-indie-ink/50 italic" data-testid={testId}>
        {label}: no data yet.
      </div>
    );
  }

  return (
    <div className="border border-indie-ink/15 bg-white p-4 flex flex-col gap-2 min-h-[260px]" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">{label}</div>
          {formula && <div className="text-[10px] font-mono text-indie-ink/45 mt-0.5">{formula}</div>}
        </div>
        <div className="inline-flex bg-indie-cream border border-indie-ink/20" data-testid={`${testId}-view-toggle`}>
          <button
            onClick={() => setView('share')}
            className={`px-2 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold ${view === 'share' ? 'bg-indie-ink text-indie-cream' : 'text-indie-ink/70'}`}
            data-testid={`${testId}-view-share`}
          >Share</button>
          <button
            onClick={() => setView('volume')}
            className={`px-2 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold ${view === 'volume' ? 'bg-indie-ink text-indie-cream' : 'text-indie-ink/70'}`}
            data-testid={`${testId}-view-volume`}
          >Volume</button>
        </div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 4, left: 4 }} stackOffset={view === 'share' ? 'expand' : 'none'}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#141B4D' }} {...AXIS} />
            <YAxis tick={{ fontSize: 10, fill: '#141B4D' }} width={38} {...AXIS} {...rateAxis} />
            <Tooltip
              {...TOOLTIP}
              formatter={(v, name) => [view === 'share' ? `${(v * 100).toFixed(1)}%` : v.toLocaleString(), name]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {bucketNames.map((n, i) => (
              <Area key={n} type="monotone" dataKey={n} stackId="1"
                stroke={colorFor(n, i)} fill={colorFor(n, i)} fillOpacity={0.8} isAnimationActive={false} />
            ))}
            {relevantBreaks.map((b) => {
              const brkMonth = parseInt(b.date.split('-')[1], 10);
              const point = data.find((d) => d.month === brkMonth);
              if (!point) return null;
              return (
                <ReferenceLine key={b.date} x={point.short} stroke="#141B4D" strokeDasharray="4 4" strokeOpacity={0.55} />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between gap-2 text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
        <span>{source}</span>
        <span className="uppercase tracking-[0.14em]">{clock} clock</span>
      </div>
    </div>
  );
}
